/**
 * RemittanceQueueChips Component
 *
 * Horizontal row of smart queue filter chips with badge counts for remittances.
 * Calls the remittance queue-counts endpoint independently of the remittances list.
 */

import React from 'react';
import { Box, Chip, Skeleton, Stack } from '@mui/material';

import { REMITTANCE_QUEUE_CHIP_CONFIG, REMITTANCE_QUEUE_KEYS } from '../../constants/statusOptions';
import { useRemittanceQueueCounts } from '../../store';

interface RemittanceQueueChipsProps {
  activeQueue?: string;
  onQueueSelect: (queue: string | undefined) => void;
  filters?: {
    dateFrom?: string;
    dateTo?: string;
    payerName?: string;
    paymentMethod?: string;
  };
}

export const RemittanceQueueChips: React.FC<RemittanceQueueChipsProps> = ({
  activeQueue,
  onQueueSelect,
  filters,
}) => {
  const { counts, isLoading } = useRemittanceQueueCounts(filters);

  if (isLoading) {
    return (
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" width={90} height={28} sx={{ borderRadius: 999 }} />
          ))}
        </Stack>
      </Box>
    );
  }

  if (!counts) return null;

  const handleClick = (queueKey: string) => {
    // Toggle: if clicking active queue, clear the filter
    if (activeQueue === queueKey) {
      onQueueSelect(undefined);
    } else {
      onQueueSelect(queueKey);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        {REMITTANCE_QUEUE_KEYS.map(key => {
          const config = REMITTANCE_QUEUE_CHIP_CONFIG[key];
          const count = counts[key as keyof typeof counts] ?? 0;
          const isActive = activeQueue === key;

          return (
            <Chip
              key={key}
              label={`${config.label} (${count})`}
              color={config.color}
              variant={isActive ? 'filled' : 'outlined'}
              size="small"
              onClick={() => handleClick(key)}
              aria-label={`${config.label}: ${count} payments`}
              sx={{
                cursor: 'pointer',
                fontWeight: isActive ? 600 : 400,
                '& .MuiChip-label': { fontSize: '0.8rem' },
              }}
            />
          );
        })}
      </Stack>
    </Box>
  );
};
